import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellIcon, CalendarClockIcon, CalendarRangeIcon, CopyIcon, DownloadIcon, EyeIcon, FilterXIcon, MailIcon, MoreHorizontalIcon, PauseIcon, PlayIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingBlock } from '@/components/LoadingBlock';
import { SectionTitle } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { downloadCsv } from '@/lib/csv';
import { fmtDateTime, fmtNumber, fmtRelative } from '@/lib/format';
import { audienceSummary, describeTrigger, describeWindow, inboxApi, payloadToCsv, RUN_STATUS_LABEL, RUN_TRIGGER_LABEL, type ReportPayload, type ReportRun, type ReportsApi, type ReportSubscription, type ReportSubscriptionInput } from '@/lib/reports';
import { useQueryState } from '@/lib/url';
import { cn } from '@/lib/utils';
import { RangeDialog, type PeriodPick } from './RangeDialog';
import { ReportEditor, type EditorJob, type EditorPerson } from './ReportEditor';
import { ReportPayloadView } from './ReportPayloadView';
import { RunDialog, RunStatusBadge } from './RunDialog';
import { fmtInZone } from './schedule';
import { templatesFor } from './templates';

interface Props {
  api: ReportsApi;
  queryKey: readonly unknown[];
  scope: 'org' | 'job';
  jobId?: string;
  jobs?: EditorJob[];
  people?: EditorPerson[];
  /** Show the personal inbox block (org and job pages both do; it is the same inbox). */
  showInbox?: boolean;
  /** Whether this person may create / edit / run reports here (Super Users on the org page, admins on a job). */
  canManage?: boolean;
  /** Organization or job time zone, offered as the default for new reports. */
  defaultTimezone?: string | null;
}

type Filter = 'all' | 'schedule' | 'event' | 'paused';

/**
 * Reports home for an organization or a job: the person's inbox, the configured reports (filterable, with every
 * action one click away), recent runs, and the dialogs: editor, run detail, previews and custom periods.
 * URL state: ?new=1|<templateKey>, ?report=<id> (edit), ?run=<id>, ?runs=<subscriptionId> (filter the runs list),
 * ?rp= / ?rpage= (page of the reports / runs lists). Sections can be linked: #your-reports (inbox), #reports, #runs.
 */
const INBOX_SHOWN = 8;
const REPORTS_PER_PAGE = 10;
const RUNS_PER_PAGE = 10;

export function ReportsPanel({ api, queryKey, scope, jobId, jobs, people, showInbox = true, canManage = true, defaultTimezone }: Props) {
  const qc = useQueryClient();
  const { refreshMe } = useAuth();
  const [params, setParams] = useQueryState();
  const catalog = useQuery({ queryKey: [...queryKey, 'catalog'], queryFn: () => api.catalog(), staleTime: 300_000 });
  const list = useQuery({ queryKey: [...queryKey, 'subscriptions'], queryFn: () => api.list() });
  const runsFilter = params.get('runs');
  const runs = useQuery({ queryKey: [...queryKey, 'runs', runsFilter ?? 'all'], queryFn: () => api.runs(runsFilter ?? undefined, 100), refetchInterval: 60_000 });
  /** Unfiltered recent runs, for each report's history strip and the summary figures. */
  const history = useQuery({ queryKey: [...queryKey, 'runs', 'history'], queryFn: () => api.runs(undefined, 200), refetchInterval: 60_000 });
  const inbox = useQuery({ queryKey: ['reports', 'inbox'], queryFn: () => inboxApi.list(), enabled: showInbox, refetchInterval: 60_000 });

  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [inboxAll, setInboxAll] = useState(false);
  // Narrowing the list starts it over from its first page.
  const pickFilter = (k: Filter) => {
    setFilter(k);
    setParams({ rp: null });
  };
  const changeSearch = (v: string) => {
    setSearch(v);
    setParams({ rp: null });
  };
  const [preview, setPreview] = useState<{ title: string; payload: ReportPayload; periodLabel?: string } | null>(null);
  const [period, setPeriod] = useState<{ mode: 'run' | 'preview'; sub: ReportSubscription } | null>(null);
  const [deleting, setDeleting] = useState<ReportSubscription | null>(null);
  const [duplicate, setDuplicate] = useState<Partial<ReportSubscriptionInput> | null>(null);

  const newParam = params.get('new');
  const editId = params.get('report');
  const runId = params.get('run');
  const items = list.data?.items ?? [];
  const editing = editId ? items.find((s) => s.id === editId) ?? null : null;
  const editorOpen = !!newParam || !!editing || !!duplicate;
  // `?new=draft`: a draft handed over by another page (the Overview's "Schedule this") through sessionStorage.
  const handedDraft = useMemo<Partial<ReportSubscriptionInput> | null>(() => {
    if (newParam !== 'draft') return null;
    try {
      const raw = sessionStorage.getItem('crewline:report-draft');
      return raw ? (JSON.parse(raw) as Partial<ReportSubscriptionInput>) : null;
    } catch {
      return null;
    }
  }, [newParam]);
  const closeEditor = () => {
    setDuplicate(null);
    setParams({ new: null, report: null });
  };
  const openNew = (template?: string) => setParams({ new: template ?? '1', report: null });

  // A stale ?report= (deleted, or not visible to this person) should not leave the URL pointing at nothing.
  useEffect(() => {
    if (editId && list.data && !editing) setParams({ report: null });
  }, [editId, list.data, editing, setParams]);

  // A link to #your-reports, #reports or #runs lands on that section once every block above it has rendered (the summary band and inbox shift the layout as they load).
  const scrolledToHash = useRef(false);
  useEffect(() => {
    if (scrolledToHash.current || !catalog.data || !list.data || runs.isPending || history.isPending || (showInbox && inbox.isPending)) return;
    const id = window.location.hash.slice(1);
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return; // not on the page yet (still loading); try again when the next query lands
    scrolledToHash.current = true;
    window.requestAnimationFrame(() => el.scrollIntoView({ block: 'start' }));
  }, [catalog.data, list.data, runs.isPending, history.isPending, inbox.isPending, showInbox]);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey });
    await qc.invalidateQueries({ queryKey: ['reports', 'inbox'] });
    void refreshMe();
  };
  const save = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: ReportSubscriptionInput }) => (id ? api.update(id, body) : api.create(body)),
    onSuccess: async (_r, v) => {
      toast.success(v.id ? 'Report saved' : 'Report created');
      await invalidate();
    },
  });
  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.update(id, { enabled }),
    onSuccess: async (r) => {
      toast.success(r.subscription.enabled ? `“${r.subscription.name}” resumed` : `“${r.subscription.name}” paused`);
      await invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.remove(id),
    onSuccess: async () => {
      toast.success('Report deleted');
      setDeleting(null);
      await invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const runNow = useMutation({
    mutationFn: ({ id, window }: { id: string; window?: PeriodPick }) => api.run(id, window ? { windowStart: window.windowStart, windowEnd: window.windowEnd } : {}),
    onSuccess: async (r) => {
      const n = r.run.recipientCount;
      toast.success(r.run.status === 'sent' ? `Sent to ${n} ${n === 1 ? 'person' : 'people'}` : r.run.status === 'skipped' ? 'Nobody to send to: check the audience and their preferences' : r.run.status === 'failed' ? `Run failed: ${r.run.error ?? 'unknown error'}` : 'Nothing to report for this period', {
        action: { label: 'Open', onClick: () => setParams({ run: r.run.id }) },
      });
      setPeriod(null);
      await invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const sendTest = useMutation({
    mutationFn: (id: string) => api.test(id),
    onSuccess: (r) => toast.success(`Test sent to ${r.sentTo}`),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const doPreview = useMutation({
    mutationFn: ({ sub, window }: { sub: ReportSubscription; window?: PeriodPick }) => api.preview(sub.id, window ? { windowStart: window.windowStart, windowEnd: window.windowEnd } : undefined).then((r) => ({ title: sub.name, payload: r.payload, periodLabel: window?.label })),
    onSuccess: (r) => {
      setPreview(r);
      setPeriod(null);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const markRead = useMutation({
    mutationFn: (ids?: string[]) => inboxApi.read(ids),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['reports', 'inbox'] });
      void refreshMe();
    },
  });

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => (filter === 'all' ? true : filter === 'paused' ? !s.enabled : s.triggerKind === filter)).filter((s) => !q || s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q) || (s.jobName ?? '').toLowerCase().includes(q));
  }, [items, filter, search]);
  const counts = { all: items.length, schedule: items.filter((s) => s.triggerKind === 'schedule').length, event: items.filter((s) => s.triggerKind === 'event').length, paused: items.filter((s) => !s.enabled).length };
  const filteredRunsOf = runsFilter ? items.find((s) => s.id === runsFilter) : null;
  // Both lists come a page at a time; the current page of each lives in the URL.
  const reportsPage = Math.max(1, Number(params.get('rp')) || 1);
  const reportsPageCount = Math.max(1, Math.ceil(visible.length / REPORTS_PER_PAGE));
  const reportsCurrent = Math.min(reportsPage, reportsPageCount);
  const visiblePage = visible.slice((reportsCurrent - 1) * REPORTS_PER_PAGE, reportsCurrent * REPORTS_PER_PAGE);
  const runItems = runs.data?.items ?? [];
  const runsPage = Math.max(1, Number(params.get('rpage')) || 1);
  const runsPageCount = Math.max(1, Math.ceil(runItems.length / RUNS_PER_PAGE));
  const runsCurrent = Math.min(runsPage, runsPageCount);
  const runsShown = runItems.slice((runsCurrent - 1) * RUNS_PER_PAGE, runsCurrent * RUNS_PER_PAGE);
  const templates = templatesFor(scope);
  const historyItems = history.data?.items ?? [];
  const historyOf = (id: string): ReportRun[] => historyItems.filter((r) => r.subscriptionId === id && r.triggeredBy !== 'test').slice(0, 8).reverse();
  const nextUp = items.filter((s) => s.enabled && s.triggerKind === 'schedule' && s.nextRunAt).sort((a, b) => (a.nextRunAt as string).localeCompare(b.nextRunAt as string))[0];
  const monthAgo = Date.now() - 30 * 86_400_000;
  const sent30 = historyItems.filter((r) => r.status === 'sent' && r.triggeredBy !== 'test' && new Date(r.startedAt).getTime() >= monthAgo);
  const reached30 = sent30.reduce((n, r) => n + r.recipientCount, 0);

  if (catalog.isError || list.isError) return <ErrorState error={catalog.error ?? list.error} onRetry={() => void Promise.all([catalog.refetch(), list.refetch()])} />;
  if (!catalog.data || !list.data) return <LoadingBlock rows={4} />;
  const inboxItems = inbox.data?.items ?? [];

  return (
    <div className="space-y-8">
      {showInbox && inboxItems.length > 0 && (
        <section id="your-reports" className="bg-card scroll-mt-20 border">
          <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
            <SectionTitle>
              <a href="#your-reports" className="flex items-center gap-2 underline-offset-4 hover:underline" title="Link to your reports">
                <BellIcon className="size-4" aria-hidden /> Your reports
                {inbox.data && inbox.data.unread > 0 && <Badge variant="default">{inbox.data.unread} new</Badge>}
              </a>
            </SectionTitle>
            {inbox.data && inbox.data.unread > 0 && (
              <Button variant="ghost" size="sm" onClick={() => markRead.mutate(undefined)}>
                Mark all read
              </Button>
            )}
          </div>
          <ul className="divide-y">
            {(inboxAll ? inboxItems : inboxItems.slice(0, INBOX_SHOWN)).map((i) => (
              <li key={i.deliveryId}>
                <button
                  type="button"
                  className={cn('hover:bg-accent/40 flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm', !i.readAt && 'bg-primary/5')}
                  onClick={() => {
                    setParams({ run: i.runId });
                    if (!i.readAt) markRead.mutate([i.deliveryId]);
                  }}
                >
                  <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', i.readAt ? 'bg-transparent' : 'bg-primary')} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{i.title}</span>
                    <span className="text-muted-foreground block truncate text-xs">{i.summaryText ?? RUN_TRIGGER_LABEL[i.triggeredBy]}</span>
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs">{fmtRelative(i.at)}</span>
                </button>
              </li>
            ))}
          </ul>
          {inboxItems.length > INBOX_SHOWN && (
            <div className="flex justify-center border-t py-1.5">
              <Button type="button" variant="ghost" size="xs" onClick={() => setInboxAll((v) => !v)}>
                {inboxAll ? 'Show fewer' : 'Show all ' + fmtNumber(inboxItems.length)}
              </Button>
            </div>
          )}
        </section>
      )}

      {/* ------------------------------------------------------------ at a glance */}
      {items.length > 0 && (
        <div className="bg-border grid grid-cols-2 gap-px border lg:grid-cols-4" role="list" aria-label="Reports at a glance">
          <Glance label="Reports" value={fmtNumber(items.length)} hint={`${counts.schedule} ${counts.schedule === 1 ? 'digest' : 'digests'} · ${counts.event} ${counts.event === 1 ? 'alert' : 'alerts'}${counts.paused ? ` · ${counts.paused} paused` : ''}`} />
          <Glance label="Next digest" value={nextUp?.nextRunAt ? fmtRelative(nextUp.nextRunAt) : '—'} hint={nextUp ? `${nextUp.name} · ${fmtInZone(nextUp.nextRunAt as string, nextUp.timezone, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}` : counts.schedule ? 'All digests are paused' : 'No digests yet'} />
          <Glance label="Watching" value={fmtNumber(items.filter((s) => s.enabled && s.triggerKind === 'event').length)} hint={counts.event ? 'alerts checked about once a minute' : 'No alerts yet'} />
          <Glance label="Sent, last 30 days" value={fmtNumber(sent30.length)} hint={sent30.length ? `${fmtNumber(reached30)} ${reached30 === 1 ? 'delivery' : 'deliveries'} to people` : 'Nothing has gone out yet'} />
        </div>
      )}

      {/* ------------------------------------------------------------ configured reports */}
      <section id="reports" className="scroll-mt-20 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SectionTitle>{scope === 'job' ? 'Reports for this job' : 'Configured reports'}</SectionTitle>
          <div className="flex flex-wrap items-center gap-2">
            {items.length > 0 && (
              <div role="radiogroup" aria-label="Filter reports" className="inline-flex border">
                {(
                  [
                    ['all', 'All'],
                    ['schedule', 'Digests'],
                    ['event', 'Alerts'],
                    ['paused', 'Paused'],
                  ] as Array<[Filter, string]>
                ).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={filter === k} onClick={() => pickFilter(k)} className={cn('flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors', filter === k ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
                    {label}
                    <span className={cn('tabular-nums', filter === k ? 'text-background/70' : 'text-muted-foreground/70')}>{counts[k]}</span>
                  </button>
                ))}
              </div>
            )}
            {items.length > 6 && (
              <div className="relative">
                <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" aria-hidden />
                <Input value={search} onChange={(e) => changeSearch(e.target.value)} placeholder="Search reports" className="h-8 w-48 pl-8 text-sm" aria-label="Search reports" />
              </div>
            )}
            {canManage && (
              <Button size="sm" onClick={() => openNew()}>
                <PlusIcon aria-hidden /> New report
              </Button>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          canManage ? (
            <div className="border border-dashed p-6">
              <div className="mb-5 text-center">
                <div className="bg-muted text-muted-foreground mx-auto mb-3 flex size-10 items-center justify-center">
                  <MailIcon className="size-5" aria-hidden />
                </div>
                <h3 className="text-sm font-semibold">No reports yet</h3>
                <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">{scope === 'job' ? 'Get a weekly digest of this job or a notice every time a send finishes. Pick a starting point, then adjust who gets it.' : 'Digests on a schedule or notices when things happen: usage, delivery, automation, sign-ups, people, help requests and billing. Pick a starting point, then adjust who gets it.'}</p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {templates.slice(0, 6).map((t) => (
                  <button key={t.key} type="button" onClick={() => openNew(t.key)} className="group hover:border-primary hover:bg-primary/5 bg-card flex items-start gap-3 border p-3 text-left transition-colors">
                    <span className="bg-muted text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground flex size-8 shrink-0 items-center justify-center transition-colors">
                      <t.icon className="size-4" aria-hidden />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{t.title}</span>
                      <span className="text-muted-foreground block text-xs leading-relaxed">{t.blurb}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" onClick={() => openNew()}>
                  See all starting points
                </Button>
              </div>
            </div>
          ) : (
            <EmptyState icon={MailIcon} title="No reports yet" description="Reports sent to you show up in your inbox above." />
          )
        ) : visible.length === 0 ? (
          <EmptyState icon={FilterXIcon} title="Nothing matches" description="Try another filter or clear the search." action={<Button variant="outline" size="sm" onClick={() => { setFilter('all'); setSearch(''); }}>Clear</Button>} className="py-10" />
        ) : (
          <div className="border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Who</TableHead>
                  <TableHead>Last run</TableHead>
                  <TableHead>Next</TableHead>
                  {canManage && <TableHead className="w-20">Active</TableHead>}
                  {canManage && <TableHead className="w-12" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiblePage.map((s) => (
                  <TableRow key={s.id} className={cn(!s.enabled && 'opacity-60', runsFilter === s.id && 'bg-primary/5')}>
                    <TableCell>
                      <button type="button" className="text-left font-medium hover:underline" onClick={() => setParams({ runs: runsFilter === s.id ? null : s.id, rpage: null })} title="Show this report’s runs">
                        {s.name}
                      </button>
                      {s.description && <div className="text-muted-foreground max-w-72 truncate text-xs">{s.description}</div>}
                      <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                        {s.jobId ? <Badge variant="outline">{s.jobName ?? 'Job'}</Badge> : scope === 'org' ? <Badge variant="outline">Organization</Badge> : null}
                        {s.triggerKind === 'schedule' ? <CalendarClockIcon className="size-3.5" aria-label="Digest" /> : <BellIcon className="size-3.5" aria-label="Alert" />}
                        <span>
                          {s.triggerKind === 'schedule' ? `${s.sections.length} ${s.sections.length === 1 ? 'section' : 'sections'}` : `${s.eventTypes.length} ${s.eventTypes.length === 1 ? 'event' : 'events'}${s.sections.length ? ` + ${s.sections.length} ${s.sections.length === 1 ? 'section' : 'sections'}` : ''}`}
                          {s.format === 'detailed' ? ' · detailed' : ''}
                          {s.attachCsv ? ' · CSV' : ''}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{s.triggerKind === 'schedule' ? (s.cronDescription ?? s.cron) : describeTrigger(s)}</div>
                      <div className="text-muted-foreground text-xs">{s.triggerKind === 'schedule' ? `${describeWindow(s.windowKind, s.windowDays)} · ${s.timezone}` : s.timezone}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{audienceSummary(s.recipients)}</div>
                      <div className="text-muted-foreground text-xs">{s.channels.map((c) => (c === 'email' ? 'email' : 'in-app')).join(' + ')}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-2">
                        <RunStatusBadge status={s.lastStatus} />
                        <RunHistory runs={historyOf(s.id)} onOpen={(id) => setParams({ run: id })} />
                      </div>
                      {s.lastRunAt && (
                        <div className="text-muted-foreground mt-0.5 text-xs" title={fmtDateTime(s.lastRunAt)}>
                          {fmtRelative(s.lastRunAt)} · {s.runCount} {s.runCount === 1 ? 'run' : 'runs'}
                        </div>
                      )}
                      {s.lastError && <div className="text-destructive max-w-56 truncate text-xs" title={s.lastError}>{s.lastError}</div>}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {s.triggerKind === 'schedule' ? (
                        s.enabled && s.nextRunAt ? (
                          <span title={fmtInZone(s.nextRunAt, s.timezone, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}>{fmtRelative(s.nextRunAt)}</span>
                        ) : (
                          '—'
                        )
                      ) : s.enabled ? (
                        <span className="flex items-center gap-1.5">
                          <span className="bg-success size-1.5 animate-pulse rounded-full" aria-hidden /> watching
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <Switch checked={s.enabled} onCheckedChange={(v) => toggleEnabled.mutate({ id: s.id, enabled: v })} aria-label={`${s.enabled ? 'Pause' : 'Resume'} ${s.name}`} />
                      </TableCell>
                    )}
                    {canManage && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Actions for ${s.name}`}>
                              <MoreHorizontalIcon />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onClick={() => doPreview.mutate({ sub: s })}>
                              <EyeIcon /> Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPeriod({ mode: 'preview', sub: s })}>
                              <CalendarRangeIcon /> Preview a period…
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => runNow.mutate({ id: s.id })}>
                              <PlayIcon /> Run now
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPeriod({ mode: 'run', sub: s })}>
                              <CalendarRangeIcon /> Run for a period…
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => sendTest.mutate(s.id)}>
                              <MailIcon /> Send me a test
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setParams({ report: s.id, new: null })}>Edit</DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                const { id: _id, jobName: _jn, cronDescription: _cd, nextRunAt: _n, lastRunAt: _l, lastStatus: _ls, lastError: _le, runCount: _rc, createdAt: _ca, updatedAt: _ua, scope: _sc, tenantId: _t, ...rest } = s;
                                setDuplicate({ ...rest, name: `${s.name} (copy)` });
                              }}
                            >
                              <CopyIcon /> Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleEnabled.mutate({ id: s.id, enabled: !s.enabled })}>{s.enabled ? <><PauseIcon /> Pause</> : <><PlayIcon /> Resume</>}</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => setDeleting(s)}>
                              <Trash2Icon /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {visible.length > REPORTS_PER_PAGE && <Pagination compact page={reportsCurrent} pageSize={REPORTS_PER_PAGE} total={visible.length} onPageChange={(p) => setParams({ rp: p > 1 ? p : null })} />}
        {items.length > 0 && <p className="text-muted-foreground text-xs">Digests with nothing to say are recorded as a quiet window and not emailed. Anyone can mute a report from Account → Notifications or the link in the email footer. Click a report’s name to see only its runs.</p>}
      </section>

      {/* ------------------------------------------------------------ runs */}
      <section id="runs" className="scroll-mt-20 space-y-3">
        <SectionTitle
          actions={
            filteredRunsOf ? (
              <Button variant="outline" size="sm" onClick={() => setParams({ runs: null, rpage: null })}>
                <FilterXIcon aria-hidden /> Showing “{filteredRunsOf.name}” · show all
              </Button>
            ) : undefined
          }
        >
          Recent runs
        </SectionTitle>
        {runs.isPending ? (
          <LoadingBlock rows={3} />
        ) : (runs.data?.items.length ?? 0) === 0 ? (
          <p className="text-muted-foreground text-sm">{filteredRunsOf ? 'This report has not run yet.' : 'Nothing has run yet. Use “Run now” on a report to see it here.'}</p>
        ) : (
          <div className="border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>How</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {runsShown.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setParams({ run: r.id })}>
                    <TableCell>
                      <div className="font-medium">{r.subscriptionName ?? r.title}</div>
                      {r.summaryText && <div className="text-muted-foreground max-w-72 truncate text-xs">{r.summaryText}</div>}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">{periodLabel(r.windowStart, r.windowEnd)}</TableCell>
                    <TableCell className="text-sm">{RUN_TRIGGER_LABEL[r.triggeredBy]}</TableCell>
                    <TableCell>
                      <RunStatusBadge status={r.status} />
                      {r.status === 'sent' && <span className="text-muted-foreground ml-2 text-xs">{r.recipientCount} {r.recipientCount === 1 ? 'recipient' : 'recipients'}{r.emailCount ? ` · ${r.emailCount} ${r.emailCount === 1 ? 'email' : 'emails'}` : ''}</span>}
                      {r.error && <div className="text-destructive max-w-64 truncate text-xs" title={r.error}>{r.error}</div>}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs" title={fmtDateTime(r.startedAt)}>
                      {fmtRelative(r.startedAt)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setParams({ run: r.id }); }}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {runItems.length > RUNS_PER_PAGE && <Pagination compact page={runsCurrent} pageSize={RUNS_PER_PAGE} total={runItems.length} onPageChange={(p) => setParams({ rpage: p > 1 ? p : null })} />}
      </section>

      {/* ------------------------------------------------------------ dialogs */}
      {editorOpen && (
        <ReportEditor
          key={editing?.id ?? (duplicate ? 'duplicate' : newParam ?? 'new')}
          open
          onClose={closeEditor}
          api={api}
          catalog={catalog.data.catalog}
          scope={scope}
          jobId={jobId}
          jobs={jobs}
          people={people}
          initial={editing}
          template={duplicate ?? handedDraft ?? (newParam && newParam !== '1' && newParam !== 'draft' ? newParam : null)}
          defaultTimezone={defaultTimezone}
          onSave={async (body) => (await save.mutateAsync({ id: editing?.id ?? null, body })).subscription}
          onCreated={(sub, then) => {
            if (then === 'run') runNow.mutate({ id: sub.id });
            else if (then === 'test') sendTest.mutate(sub.id);
          }}
        />
      )}

      <RunDialog api={api} runId={runId} onClose={() => setParams({ run: null })} canManage={canManage} onEdit={(sid) => setParams({ run: null, report: sid })} onRunPeriod={(sid) => { const sub = items.find((s) => s.id === sid); if (sub) { setParams({ run: null }); setPeriod({ mode: 'run', sub }); } }} />

      {period && (
        <RangeDialog
          open
          onClose={() => setPeriod(null)}
          title={period.mode === 'run' ? `Run “${period.sub.name}” for a period` : `Preview “${period.sub.name}” for a period`}
          description={period.mode === 'run' ? 'Computes the report for the dates you pick and sends it to its audience now. The regular schedule is unaffected.' : 'Computes the report for the dates you pick. Nothing is sent.'}
          confirmLabel={period.mode === 'run' ? 'Run and send' : 'Preview'}
          timezone={period.sub.timezone}
          loading={runNow.isPending || doPreview.isPending}
          onConfirm={(w) => (period.mode === 'run' ? runNow.mutate({ id: period.sub.id, window: w }) : doPreview.mutate({ sub: period.sub, window: w }))}
        />
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
          <DialogHeader className="border-b px-6 pt-5 pb-4 pr-12">
            <DialogTitle className="text-lg">Preview · {preview?.title}</DialogTitle>
            <DialogDescription>{preview?.periodLabel ? `Computed just now for ${preview.periodLabel}. Nothing was sent.` : 'Computed just now for the report’s own period. Nothing was sent.'}</DialogDescription>
            {preview && (
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const { filename, csv } = payloadToCsv(preview.payload, preview.title);
                    downloadCsv(filename, csv);
                  }}
                >
                  <DownloadIcon aria-hidden /> Download CSV
                </Button>
              </div>
            )}
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{preview && <ReportPayloadView payload={preview.payload} compact />}</div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete “${deleting?.name}”?`}
        description="Past runs are removed with it. Recipients simply stop receiving it. Pausing keeps everything if you might want it back."
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
      />
    </div>
  );
}

function Glance({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div role="listitem" className="bg-card flex min-w-0 flex-col gap-1 p-4">
      <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{label}</span>
      <span className="font-display text-3xl leading-none tracking-tight">{value}</span>
      {hint && <span className="text-muted-foreground truncate text-xs">{hint}</span>}
    </div>
  );
}

/** The last few runs as small squares, oldest to newest, each opening its run. */
function RunHistory({ runs, onOpen }: { runs: ReportRun[]; onOpen: (runId: string) => void }) {
  if (runs.length === 0) return null;
  return (
    <span className="flex items-center gap-0.5" aria-label={`Last ${runs.length} runs`}>
      {runs.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => onOpen(r.id)}
          title={`${RUN_STATUS_LABEL[r.status]} · ${RUN_TRIGGER_LABEL[r.triggeredBy]} · ${fmtDateTime(r.startedAt)}`}
          aria-label={`${RUN_STATUS_LABEL[r.status]}, ${fmtRelative(r.startedAt)}`}
          className={cn('size-2.5 transition-transform hover:scale-125', r.status === 'sent' ? 'bg-success' : r.status === 'failed' ? 'bg-destructive' : r.status === 'skipped' ? 'bg-warning' : 'bg-muted-foreground/30')}
        />
      ))}
    </span>
  );
}

function periodLabel(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const sameDay = start.toDateString() === end.toDateString();
  const d = (x: Date, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-US', o).format(x);
  if (sameDay) return `${d(start, { month: 'short', day: 'numeric' })}, ${d(start, { hour: 'numeric', minute: '2-digit' })} – ${d(end, { hour: 'numeric', minute: '2-digit' })}`;
  return `${d(start, { month: 'short', day: 'numeric' })} – ${d(end, { month: 'short', day: 'numeric' })}`;
}
